/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @author Zdenka Valkučáková <zdenka.valkucakova@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-env browser */

import isString from 'lodash/isString';
import StringUtil from './string/string-util';

/**
 * @import { IDateUTC } from './date'
 * @import { TimeZone } from './time'
 */

/** @type {ISOString} */
export const minISOString = '0000-01-01T00:00:00.000Z';
export const minISOStringTime = new Date(minISOString).getTime();

/** @type {ISOString} */
export const maxISOString = '9999-12-31T23:59:59.999Z';
export const maxISOStringTime = new Date(maxISOString).getTime();

/**
 * Pro jednotnost stejná chyba jako při {@link Date.prototype.toISOString},
 * když je Date NaN nebo mimo rozsah. Získá se pomocí `new Date(NaN).toISOString()`.
 */
export const isoStringRangeError = /** @type {RangeError} */ (
  (() => {
    try {
      new Date(NaN).toISOString();
    } catch (error) {
      return error;
    }
  })()
);

/**
 * ISO 8601 reprezentace data a času v pevném formátu definovaném
 * {@link Date.prototype.toISOString} (povinné milisekundy, TZ UTC).
 *
 * Kvůli požadavku na lexikografické seřazení není podporovaný celý rozsah
 * {@link Date}, ale jen '0000-01-01T00:00:00.000Z' až '9999-12-31T23:59:59.999Z'.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format
 * @typedef {`${number}-${number}-${number}T${number}:${number}:${number}.${number}Z`} ISOString
 */

/**
 * Zjistí, zda je hodnota {@link ISOString}.
 * @param {any} value
 * @returns {value is ISOString}
 */
export function isISOString(value) {
  if (!isString(value) || isNotISOString(value)) {
    return false;
  }
  const date = new Date(value);
  const time = date.getTime();
  return !isNaN(time) && date.toISOString() === value;
}

/**
 * Zjistí, zda je {@link date} v rozsahu {@link ISOString}.
 * @param {IDateUTC} date
 * @returns {boolean}
 */
export function isDateISOStringRange(date) {
  const time = date.getTime();
  return !isNaN(time) && time >= minISOStringTime && time <= maxISOStringTime;
}

/**
 * Vyhodí, pokud {@link date} není v rozsahu {@link ISOString}.
 * @param {IDateUTC} date
 * @returns {IDateUTC}
 * @throws {RangeError} Pokud je date mimo {@link ISOString} rozsah.
 */
export function assertIsDateISOStringRange(date) {
  if (!isDateISOStringRange(date)) {
    throw isoStringRangeError;
  }
  return date;
}

/**
 * Porovná dvě data.
 * @see {@link Array.prototype.sort}
 * @see {@link Array.prototype.toSorted}
 * @param {ISOString} date1
 * @param {ISOString} date2
 * @returns {number} Vrátí negativní číslo, pokud je první argument menší jak
 * druhý argument, nulu pokud jsou stejné, jinak pozitivní číslo.
 */
export function compare(date1, date2) {
  // Využívá toho, že ISOString pevný formát je lexikograficky porovnatelný.
  return StringUtil.compare(date1, date2);
}

/**
 * Porovná dvě data, zda je date1 menší než date2.
 * @param {ISOString} date1
 * @param {ISOString} date2
 * @returns {boolean}
 */
export function isLess(date1, date2) {
  // Využívá toho, že ISOString pevný formát je lexikograficky porovnatelný.
  return StringUtil.isLess(date1, date2);
}

/**
 * Porovná dvě data, zda je date1 menší nebo rovno než date2.
 * @param {ISOString} date1
 * @param {ISOString} date2
 * @returns {boolean}
 */
export function isLessOrEqual(date1, date2) {
  // Využíváme toho, že ISOString pevný formát je lexikograficky porovnatelný.
  return StringUtil.isLessOrEqual(date1, date2);
}

/**
 * Rychlý a levný test pro vyloučení, že výsledek z {@link Date.prototype.toISOString}
 * {@link dateISOString} určitě není {@link ISOString} kvůli rozsahu,
 * tj. že délka není 24 znaků.
 *
 * Vrácení `false` neznamená, že {@link dateISOString} je {@link ISOString},
 * proto zde není type guard.
 * @param {ReturnType<IDateUTC['toISOString']>} dateISOString
 * @returns {boolean}
 */
export function isNotISOString(dateISOString) {
  return dateISOString.length !== 24;
}

/**
 * Převede {@link date} na {@link ISOString}.
 * @param {IDateUTC} date
 * @returns {ISOString}
 * @throws {RangeError} Pokud je {@link date} NaN nebo je {@link Date} mimo rozsah
 * od {@link minISOStringTime} do {@link maxISOStringTime}.
 */
export function toISOString(date) {
  // Může vyhodit, pokud je date NaN nebo je mimo rozsah Date.
  const isoString = date.toISOString();
  if (isNotISOString(isoString)) {
    throw isoStringRangeError;
  }
  return /** @type {ISOString} */ (isoString);
}

/**
 * Zkrátí o milisekundy a případně i sekundy, pokud jsou nulové.
 *
 * Tento zkrácený formát akceptuje např. Date konstruktor.
 * @param {ISOString} isoString
 * @returns {string}
 */
export function shorten(isoString) {
  if (isoString.endsWith(':00.000Z')) {
    return isoString.replace(':00.000Z', 'Z');
  } else if (isoString.endsWith('.000Z')) {
    return isoString.replace('.000Z', 'Z');
  }
  return isoString;
}

/**
 * Převede datum a čas ve formátu ISO 8601 (UTC) na lokální čas
 * ve zadaném časovém pásmu a lokalizovaném formátu.
 * @param {ISOString} isoString Datum a čas ve formátu ISO 8601.
 * @param {TimeZone | undefined} timezone Časové pásmo (např. 'Europe/Prague').
 * Pokud není zadáno, použije se systémové časové pásmo.
 * @param {string} [locale] Jazyková lokalizace pro formátování výstupu.
 * Pokud není zadána, použije se výchozí lokalizace systému
 * (Intl.DateTimeFormat().resolvedOptions().locale), nebo 'en-GB' jako fallback.
 * @returns {string}
 */
export function toLocalDate(isoString, timezone, locale) {
  const resolvedLocale =
    locale ||
    (typeof Intl === 'undefined'
      ? 'en-GB'
      : Intl.DateTimeFormat().resolvedOptions().locale);

  return new Date(isoString).toLocaleString(resolvedLocale, {
    timeZone: timezone
  });
}
